import React from 'react';
import styled, {
  FlattenSimpleInterpolation,
  css,
} from 'styled-components/macro';
import {
  FacebookIcon,
  DealerRaterIcon,
  InstagramIcon,
  XIcon,
  YelpIcon,
  TikTokIcon,
  PinterestIcon,
  WhatsappIcon,
  LinkedinIcon,
  BniIcon,
  YoutubeIcon,
  ZeeMeeIcon,
  EdmundsIcon,
  CarsComIcon,
} from '../assets';
import { LP_DESIGN_COMMON_COLORS, LP_DESIGN_SOCIAL_MEDIA } from '../constants';

export type IconType =
  | 'x'
  | 'dealerrater'
  | 'instagram'
  | 'facebook'
  | 'yelp'
  | 'tiktok'
  | 'pinterest'
  | 'whatsapp'
  | 'linkedin'
  | 'bni'
  | 'youtube'
  | 'edmunds'
  | 'zeemee'
  | 'carsdotcom';

const commonColors = {
  white: '#ffffff',
  transparent: 'transparent',
  charcoal_gray: '#212529',
  cherryRed: '#D11C2F',
  black: '#000000',
};

const ICON_MAPPER: Record<IconType, JSX.Element> = {
  facebook: <FacebookIcon />,
  dealerrater: <DealerRaterIcon />,
  instagram: <InstagramIcon />,
  x: <XIcon />,
  yelp: <YelpIcon />,
  tiktok: <TikTokIcon />,
  pinterest: <PinterestIcon />,
  whatsapp: <WhatsappIcon />,
  linkedin: <LinkedinIcon />,
  bni: <BniIcon />,
  youtube: <YoutubeIcon />,
  edmunds: <EdmundsIcon />,
  zeemee: <ZeeMeeIcon />,
  carsdotcom: <CarsComIcon />,
};

const ICON_BACKGROUND_MAP: Record<IconType, string> = {
  facebook: '#1877f2',
  dealerrater: commonColors.white,
  instagram:
    'linear-gradient(45deg, #FAAD4F 14.62%, #DD2A7B 39.39%, #9537B0 58.5%, #515BD4 85.4%)',
  x: commonColors.black,
  yelp: '#FF1B1A',
  tiktok: 'rgba(28, 28, 27, 1)',
  pinterest: '#FF0000',
  whatsapp: '#00E510',
  linkedin: '#007EBB',
  bni: commonColors.white,
  youtube: '#FF0209',
  edmunds: '#007EE5',
  zeemee: '#F16822',
  carsdotcom: '#65299D',
};

const COMMON_DESIGN_STYLES: Record<
  LP_DESIGN_SOCIAL_MEDIA,
  FlattenSimpleInterpolation
> = {
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: css`
    background: ${commonColors.white};
    border: 0.5px solid ${LP_DESIGN_COMMON_COLORS.GRAY20};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: css`
    background: ${commonColors.black};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: css`
    background: ${commonColors.black};
    border-radius: 4px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: css`
    background: ${commonColors.white};
    border: 0.5px solid ${LP_DESIGN_COMMON_COLORS.GRAY20};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: css`
    background: ${commonColors.black};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: css`
    background: ${commonColors.black};
    border-radius: 32px;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.WHITE]: css`
    background: transparent;
  `,
  [LP_DESIGN_SOCIAL_MEDIA.BLACK]: css``,
};

const createIconStyle = (fillColor?: string, strokeColor?: string) => css`
  svg path {
    ${fillColor ? `fill: ${fillColor};` : ''}
    ${strokeColor ? `stroke: ${strokeColor};` : ''}
  }
`;

const commonStyles = {
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: createIconStyle(
    commonColors.charcoal_gray
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: createIconStyle(
    commonColors.charcoal_gray
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(commonColors.white),
  [LP_DESIGN_SOCIAL_MEDIA.WHITE]: createIconStyle(
    commonColors.white,
    undefined
  ),
  [LP_DESIGN_SOCIAL_MEDIA.BLACK]: createIconStyle(commonColors.charcoal_gray),
};

const ICON_STYLES: Record<
  IconType,
  Record<LP_DESIGN_SOCIAL_MEDIA, FlattenSimpleInterpolation>
> = {
  dealerrater: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      commonColors.charcoal_gray
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      commonColors.charcoal_gray
    ),
  },
  instagram: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: createIconStyle(commonColors.black),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      undefined,
      commonColors.white
    ),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK]: createIconStyle(
      undefined,
      commonColors.charcoal_gray
    ),
    [LP_DESIGN_SOCIAL_MEDIA.WHITE]: createIconStyle(
      commonColors.transparent,
      commonColors.white
    ),
  },
  bni: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(
      commonColors.cherryRed
    ),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(
      commonColors.cherryRed
    ),
  },
  zeemee: {
    ...commonStyles,
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE]: createIconStyle(undefined),
    [LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE]: createIconStyle(undefined),
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_SQUARE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK_CIRCLE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.BLACK]: css`
      ${createIconStyle(
        commonColors.charcoal_gray,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.white};
      }
    `,
    [LP_DESIGN_SOCIAL_MEDIA.WHITE]: css`
      ${createIconStyle(
        commonColors.white,
        undefined
      )} svg path:nth-of-type(2) {
        fill: ${commonColors.charcoal_gray};
      }
    `,
  },
  carsdotcom: {
    ...commonStyles,
  },
  x: {
    ...commonStyles,
  },
  yelp: {
    ...commonStyles,
  },
  tiktok: {
    ...commonStyles,
  },
  pinterest: {
    ...commonStyles,
  },
  whatsapp: {
    ...commonStyles,
  },
  linkedin: {
    ...commonStyles,
  },
  youtube: {
    ...commonStyles,
  },
  edmunds: {
    ...commonStyles,
  },
  facebook: {
    ...commonStyles,
  },
};

export const generateIconCss = (
  icon: IconType,
  design: LP_DESIGN_SOCIAL_MEDIA,
  useOutlinedIcon: boolean
) => {
  const commonCss = COMMON_DESIGN_STYLES[design];
  const iconCss = ICON_STYLES[icon][design];

  if (
    design === LP_DESIGN_SOCIAL_MEDIA.COLORS_SQUARE ||
    design === LP_DESIGN_SOCIAL_MEDIA.COLORS_CIRCLE
  ) {
    return css`
      ${commonCss}
      ${iconCss}
      background: ${ICON_BACKGROUND_MAP[icon]};
    `;
  }

  if (useOutlinedIcon && design === LP_DESIGN_SOCIAL_MEDIA.WHITE) {
    return css`
      ${commonCss}
      ${iconCss}
      ${createIconStyle(commonColors.transparent, commonColors.charcoal_gray)}
    `;
  }

  if (
    useOutlinedIcon &&
    (design === LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE ||
      design === LP_DESIGN_SOCIAL_MEDIA.WHITE_CIRCLE)
  ) {
    return css`
      ${commonCss}
      ${iconCss}
       border: 1px solid ${LP_DESIGN_COMMON_COLORS.GRAY20};
    `;
  }

  return css`
    ${commonCss}
    ${iconCss}
  `;
};

interface SocialIconProps {
  design: LP_DESIGN_SOCIAL_MEDIA;
  icon: IconType;
  size?: number;
  svgSize?: number;
  useOutlinedIcon?: boolean;
}

interface StyledIconProps {
  iconCss: FlattenSimpleInterpolation;
  size: number;
  svgSize: number;
}

const StyledIcon = styled.div<StyledIconProps>`
  ${({ iconCss }) => iconCss}
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ svgSize }) => svgSize}px;
    height: ${({ svgSize }) => svgSize}px;
  }
`;

export const SocialIcon = ({
  design,
  icon,
  size = 36,
  svgSize = 24,
  useOutlinedIcon = false,
}: SocialIconProps) => {
  const iconCss = generateIconCss(icon, design, useOutlinedIcon);

  return (
    <StyledIcon iconCss={iconCss} size={size} svgSize={svgSize}>
      {ICON_MAPPER[icon]}
    </StyledIcon>
  );
};
